import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import i18n from '../i18n';

const Services = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    { 
      key: 'services.item1', 
      number: '01', 
      descriptionKey: 'services.item1.description',
      technologiesKey: 'services.item1.technologies',
      color: 'from-blue-500 to-cyan-500',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 12h8M8 16h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      )
    },
    { 
      key: 'services.item2', 
      number: '02', 
      descriptionKey: 'services.item2.description',
      technologiesKey: 'services.item2.technologies',
      color: 'from-green-500 to-emerald-500',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      )
    },
    { 
      key: 'services.item3', 
      number: '03', 
      descriptionKey: 'services.item3.description',
      technologiesKey: 'services.item3.technologies',
      color: 'from-purple-500 to-pink-500',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V17C17 18.1 16.1 19 15 19H9C7.9 19 7 18.1 7 17V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      key: 'services.item4', 
      number: '04', 
      descriptionKey: 'services.item4.description',
      technologiesKey: 'services.item4.technologies',
      color: 'from-orange-500 to-red-500',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4 4 4 0 004-4V5z" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      )
    },
    { 
      key: 'services.item5', 
      number: '05', 
      descriptionKey: 'services.item5.description',
      technologiesKey: 'services.item5.technologies',
      color: 'from-indigo-500 to-purple-500',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 6V10M12 14H12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      )
    },
    { 
      key: 'services.item6', 
      number: '06', 
      descriptionKey: 'services.item6.description',
      technologiesKey: 'services.item6.technologies',
      color: 'from-teal-500 to-cyan-500',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 12L10 14L16 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const toggleService = (index) => {
    setExpandedService(expandedService === index ? null : index);
  };

  return (
    <section id="services" className="py-20 relative overflow-hidden" ref={ref}>
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-blue-900/20 to-slate-900" />
      
      {/* Professional Background with Code Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50fGVufDB8fHxibHVlfDE3NTUxODIzNDd8MA&ixlib=rb-4.1.0&q=85)'
        }}
      />
      
      {/* Color Accent Overlays */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(168,85,247,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(34,197,94,0.02)_1px,transparent_1px),linear-gradient(-45deg,rgba(34,197,94,0.02)_1px,transparent_1px)] [background-size:50px_50px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Enhanced Section Title */}
          <motion.div
            variants={itemVariants}
            className="mb-16"
          >
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-4 relative inline-block"
            >
              {t('services.title')}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-green-500 rounded-full"
                initial={{ scaleX: 0, originX: 0 }}
                animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </motion.h2>
          </motion.div>

          {/* Enhanced Services List */}
          <motion.div 
            className="space-y-4"
            variants={containerVariants}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.key}
                variants={itemVariants}
                className="group relative"
              >
                <motion.div
                  className="bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-lg overflow-hidden transition-all duration-300 hover:border-slate-600 relative"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Gradient Accent */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${service.color} opacity-5`} />
                  
                  {/* Service Header - CLICKABLE with better visual hints */}
                  <motion.div
                    className="flex items-center py-6 px-6 cursor-pointer relative z-10 group"
                    onClick={() => toggleService(index)}
                    whileHover={{ backgroundColor: "rgba(71, 85, 105, 0.1)" }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expandedService === index}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleService(index);
                      }
                    }}
                  >
                    {/* Enhanced Service Number */}
                    <motion.div 
                      className={`text-2xl md:text-3xl font-bold w-16 md:w-20 text-transparent bg-gradient-to-r ${service.color} bg-clip-text`}
                    >
                      {service.number}
                    </motion.div>

                    {/* Enhanced Service Icon */}
                    <motion.div 
                      className={`mr-6 p-3 rounded-lg bg-gradient-to-r ${service.color} text-white shadow-lg`}
                      whileHover={{ scale: 1.05 }}
                    >
                      {service.icon}
                    </motion.div>

                    {/* Service Name */}
                    <motion.h3 
                      className="text-lg md:text-xl text-slate-200 font-medium flex-1 text-left"
                    >
                      {t(service.key)}
                    </motion.h3>

                    {/* Enhanced Transparent Animated Expand/Collapse Icon */}
                    <motion.div className="flex items-center space-x-3 text-slate-400 text-sm">
                      {/* Transparent Animated Icon */}
                      <motion.div 
                        className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer relative overflow-hidden border-2 border-slate-600 bg-slate-800/30 backdrop-blur-sm"
                        animate={{ 
                          scale: expandedService === index ? 1.05 : 1,
                          rotate: expandedService === index ? 180 : 0,
                          borderColor: expandedService === index ? 'rgba(148, 163, 184, 0.6)' : 'rgba(100, 116, 139, 0.4)',
                          backgroundColor: expandedService === index ? 'rgba(51, 65, 85, 0.4)' : 'rgba(51, 65, 85, 0.3)'
                        }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        whileHover={{ 
                          scale: 1.1,
                          borderColor: 'rgba(148, 163, 184, 0.8)',
                          backgroundColor: 'rgba(51, 65, 85, 0.5)',
                          boxShadow: '0 0 20px rgba(148, 163, 184, 0.1)'
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {/* Subtle glowing background effect */}
                        <motion.div
                          className="absolute inset-0 rounded-full bg-slate-300 opacity-0"
                          whileHover={{ opacity: 0.05 }}
                          transition={{ duration: 0.2 }}
                        />
                        
                        {/* Transparent Chevron Icon */}
                        <motion.div
                          className="relative z-10 flex items-center justify-center"
                          animate={{
                            y: expandedService === index ? 1 : 0
                          }}
                          transition={{ duration: 0.2 }}
                        >
                          <svg 
                            className="w-5 h-5 text-slate-300" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <motion.path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M19 9l-7 7-7-7"
                              animate={{
                                d: expandedService === index ? 
                                    "M5 15l7-7 7 7" : 
                                    "M19 9l-7 7-7-7",
                                stroke: expandedService === index ? 
                                    "rgba(203, 213, 225, 0.9)" : 
                                    "rgba(148, 163, 184, 0.8)"
                              }}
                              transition={{ duration: 0.3, ease: "easeOut" }}
                            />
                          </svg>
                        </motion.div>

                        {/* Subtle ripple effect on click */}
                        <motion.div
                          className="absolute inset-0 rounded-full bg-slate-200 opacity-0"
                          animate={{
                            scale: expandedService === index ? [1, 1.2, 1] : 1,
                            opacity: expandedService === index ? [0, 0.15, 0] : 0
                          }}
                          transition={{ duration: 0.4 }}
                        />
                      </motion.div>
                      
                      {/* Subtle text hint (hidden on mobile) */}
                      <motion.span 
                        className="hidden md:inline group-hover:text-slate-300 transition-colors text-xs opacity-60"
                        animate={{
                          opacity: expandedService === index ? 0.8 : 0.6,
                          x: expandedService === index ? 2 : 0
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {expandedService === index ? 
                          (i18n.language === 'uk' ? 'Згорнути' : 'Collapse') : 
                          (i18n.language === 'uk' ? 'Розгорнути' : 'Expand')
                        }
                      </motion.span>
                    </motion.div>
                  </motion.div>

                  {/* Service Details - COLLAPSIBLE with optimized animation */}
                  <AnimatePresence mode="wait">
                    {expandedService === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ 
                          opacity: 1, 
                          height: "auto",
                          transition: {
                            height: { duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] },
                            opacity: { duration: 0.25, delay: 0.1 }
                          }
                        }}
                        exit={{ 
                          opacity: 0, 
                          height: 0,
                          transition: {
                            height: { duration: 0.25, ease: [0.04, 0.62, 0.23, 0.98] },
                            opacity: { duration: 0.2 }
                          }
                        }}
                        className="overflow-hidden relative z-10"
                        style={{ willChange: 'height, opacity' }}
                      >
                        <div className="px-6 pb-6 border-t border-slate-700/50">
                          <div className="pt-4 space-y-4">
                            <div>
                              <h4 className="text-slate-300 font-medium mb-2">
                                {i18n.language === 'uk' ? 'Опис:' : 'Description:'}
                              </h4>
                              <p className="text-slate-400 leading-relaxed">
                                {t(service.descriptionKey)}
                              </p>
                            </div>
                            <div>
                              <h4 className="text-slate-300 font-medium mb-2">
                                {i18n.language === 'uk' ? 'Технології:' : 'Technologies:'}
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {t(service.technologiesKey).split(', ').map((tech, techIndex) => (
                                  <motion.span 
                                    key={techIndex}
                                    className={`px-3 py-1 bg-gradient-to-r ${service.color} text-white text-sm rounded-full shadow-md`}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ 
                                      opacity: 1, 
                                      scale: 1,
                                      transition: { duration: 0.2, delay: techIndex * 0.05 }
                                    }}
                                  >
                                    {tech}
                                  </motion.span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;